import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { LEN } from "@/lib/limites";

const EMPTY = { to: "", cc: "", subject: "", message: "" };

export default function EnviarDecaEmail({ decaId, open, onOpenChange, trigger }) {
  const [email, setEmail] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sendEnabled, setSendEnabled] = useState(false);
  const [smtpReady, setSmtpReady] = useState(false);

  useEffect(() => {
    if (!open || !decaId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: sug } = await api.get(`/api/v1/decas/${decaId}/email-sugerido`);
        if (cancelled) return;
        setEmail({
          to: sug.to || "",
          cc: sug.cc || "",
          subject: sug.subject || "",
          message: sug.message || "",
        });
        setSendEnabled(!!sug.send_enabled);
        setSmtpReady(!!sug.smtp_ready);
      } catch { /* el usuario puede escribirlo */ }
    })();
    return () => { cancelled = true; };
  }, [open, decaId]);

  const enviar = async () => {
    if (!email.to) {
      toast.error("A" + String.fromCharCode(0xf1) + "ade una direcci" + String.fromCharCode(0xf3) + "n de destinatario");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/api/v1/decas/${decaId}/enviar-email`, {
        to: email.to,
        cc: email.cc ? email.cc.split(",").map((s) => s.trim()).filter(Boolean) : null,
        subject: email.subject || null,
        message: email.message || null,
      });
      if (res.data.dry_run) {
        toast.info("Modo dry-run: guardado en outbox, no se ha enviado.");
      } else if (res.data.sent) {
        toast.success("Correo enviado (PDF adjunto)");
      }
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enviar DeCA por email</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Revisa destinatario, asunto y texto. Esto es exactamente lo que llegar{String.fromCharCode(0xe1)}. Puedes editarlo o cancelar: el DeCA no se env{String.fromCharCode(0xed)}a solo.
          </p>
          <Field label="Destinatario (to) *">
            <Input type="email" maxLength={LEN.email} value={email.to} onChange={(e) => setEmail({ ...email, to: e.target.value })} placeholder="transportista@empresa.com" data-testid="email-to"/>
          </Field>
          <Field label="Copia (cc, coma)">
            <Input maxLength={500} value={email.cc} onChange={(e) => setEmail({ ...email, cc: e.target.value })} placeholder="op1@empresa.com, op2@empresa.com"/>
          </Field>
          <Field label="Asunto">
            <Input maxLength={LEN.emailSubject} value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} data-testid="email-subject"/>
          </Field>
          <Field label="Mensaje">
            <Textarea rows={10} maxLength={LEN.emailMessage} value={email.message} onChange={(e) => setEmail({ ...email, message: e.target.value })} className="font-mono text-xs" data-testid="email-message"/>
          </Field>
          {sendEnabled && smtpReady ? (
            <div className="text-xs border border-emerald-200 bg-emerald-50 rounded p-2 text-emerald-900">
              Se enviar{String.fromCharCode(0xe1)} de verdad, con el PDF adjunto, a las direcciones de arriba.
            </div>
          ) : (
            <div className="text-xs border border-amber-200 bg-amber-50 rounded p-2 text-amber-800">
              <b>Modo dry-run:</b> el env{String.fromCharCode(0xed)}o real est{String.fromCharCode(0xe1)} desactivado. Falta SMTP en el servidor (SMTP_HOST, SMTP_FROM y EMAIL_SEND_ENABLED=true). Ahora solo se guarda una copia en el outbox.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-email-send">
            {sendEnabled && smtpReady ? "Enviar" : "Encolar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BotonEnviarEmail({ onClick, size = "sm" }) {
  return (
    <Button variant="outline" size={size} onClick={onClick} data-testid="btn-email">
      <Mail className="h-4 w-4 mr-2"/> Enviar por email
    </Button>
  );
}
